import { BaseAPIAdapter } from './BaseAPIAdapter';
import { FlaskLocalAdapter } from './FlaskLocalAdapter';
import { ServerlessAdapter } from './ServerlessAdapter';
import { getAPIMode, isElectronMode, type APIMode } from '../../utils/apiMode';
import { getLocalApiUrl, getServerlessApiUrl } from '../urlResolver';

// Re-export type for backward compatibility
export type { APIMode };

/**
 * Factory class to create the appropriate API adapter based on configuration
 */
export class APIAdapterFactory {
  private static instance: BaseAPIAdapter | null = null;
  private static currentMode: APIMode | null = null;

  /**
   * Get or create an API adapter instance
   * @param mode - The API mode to use (local or serverless)
   * @param baseURL - Optional base URL override
   * @returns The appropriate API adapter instance
   */
  static async getAdapter(mode?: APIMode, baseURL?: string): Promise<BaseAPIAdapter> {
    // Determine the mode using centralized utility
    const targetMode = mode || getAPIMode();

    // Return existing instance if mode hasn't changed
    if (this.instance && this.currentMode === targetMode && !baseURL) {
      return this.instance;
    }

    // URLs come from urlResolver, like every other API call in the app. This
    // used to fall back to production/staging/local URLs published in a
    // hand-kept backend JSON file, a second answer to "which API" that
    // urlResolver never consulted.
    if (isElectronMode()) {
      // In Electron desktop mode, always use Flask local adapter
      const desktopUrl = getLocalApiUrl();
      console.log(
        '[APIAdapterFactory] Desktop mode detected - using Flask local backend:',
        desktopUrl
      );
      this.instance = new FlaskLocalAdapter(desktopUrl);
    } else if (targetMode === 'serverless') {
      const url = baseURL || getServerlessApiUrl();
      if (!url) {
        throw new Error(
          'Serverless API URL is not configured (VITE_SERVERLESS_API_URL). No fallbacks.'
        );
      }
      this.instance = new ServerlessAdapter(url);
    } else {
      this.instance = new FlaskLocalAdapter(baseURL || getLocalApiUrl());
    }

    this.currentMode = targetMode;
    return this.instance;
  }

  /**
   * Force reset the cached instance
   */
  static reset(): void {
    this.instance = null;
    this.currentMode = null;
  }

  /**
   * Get the current API mode
   */
  static getCurrentMode(): APIMode | null {
    return this.currentMode;
  }
}
